import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../components/Auth/AuthContext';
import logger from '../../utils/logger';
import brand from '../../config/brand';
import { ADMIN_PATH } from '../../config/adminPath';
import LanguageToggle from '../../components/common/LanguageToggle';
import {
  User, Lock, Eye, EyeOff, Mail, AlertCircle, CheckCircle, Crown, ShieldCheck, ArrowLeft
} from 'lucide-react';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const { t } = useTranslation('admin');

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isCollaborator = searchParams.get('collaborator') === 'true';
  const forcedSpace = searchParams.get('space');

  const spaceType = forcedSpace || location.state?.spaceType ||
    (location.pathname.includes('admin') ? 'admin' :
     location.pathname.includes('client') ? 'client' :
     location.pathname.includes('courtier') ? 'partner' : null);

  // Le flux de récupération "Administrateur" (3 étapes) est réservé à la connexion admin.
  // Les autres espaces (client, courtier…) utilisent la réinitialisation classique par email.
  const isAdminLogin = spaceType === 'admin' || location.pathname.includes(ADMIN_PATH);

  useEffect(() => {
    if (isCollaborator) {
      const collaboratorData = sessionStorage.getItem('newCollaboratorAccount');
      if (collaboratorData) {
        const account = JSON.parse(collaboratorData);
        setFormData({ email: account.email, password: '' });
        setSuccess(t('login.collaboratorCreated'));
        sessionStorage.removeItem('newCollaboratorAccount');
      }
    }
  }, [isCollaborator, searchParams, t]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await login(formData);
      logger.debug('🔍 Login result:', result);

      if (result.requires_2fa) {
        navigate('/verify-2fa', { state: { tempToken: result.temp_token, userId: result.user_id } });
        return;
      }

      if (result.success) {
        const user = result.user;
        setSuccess(t('login.successWelcome', { name: user.first_name }));
        const isUserCollaborator = user.is_collaborator || isCollaborator;

        if (isUserCollaborator && user.role === 'partner') {
          navigate('/courtier-dashboard', { replace: true });
        } else if (forcedSpace === 'client') {
          navigate('/client-dashboard', { replace: true });
        } else if (user.role === 'super_admin') {
          if (spaceType === 'admin') navigate('/admin', { replace: true });
          else if (spaceType === 'client') navigate('/client-dashboard', { replace: true });
          else if (spaceType === 'partner') navigate('/courtier-dashboard', { replace: true });
          else navigate('/admin', { replace: true });
        } else if (user.role === 'admin') {
          navigate('/admin', { replace: true });
        } else if (user.role === 'client') {
          navigate('/client-dashboard', { replace: true });
        } else if (user.role === 'partner') {
          navigate('/courtier-dashboard', { replace: true });
        } else if (user.role === 'brokoli') {
          navigate('/brokoli-dashboard', { replace: true });
        } else {
          navigate('/client-dashboard', { replace: true });
        }
      } else {
        if (result.error === 'SPACE_DISABLED') setError(t('login.errorSpaceDisabled'));
        else if (result.error === 'ACCOUNT_DISABLED') setError(t('login.errorAccountDisabled'));
        else setError(result.error || t('login.errorGeneric'));
      }
    } catch (err) {
      setError(t('login.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  const getSpaceTitle = () => {
    if (isCollaborator) return t('login.titleCollaborator');
    switch (spaceType) {
      case 'admin': return t('login.titleAdmin');
      case 'client': return t('login.titleClient');
      case 'partner': return t('login.titlePartner');
      default: return t('login.title');
    }
  };

  const SpaceIcon = spaceType === 'admin' ? Crown : User;

  const trustPoints = [
    { icon: ShieldCheck, label: t('login.trustSsl') },
    { icon: Lock, label: t('login.trustData') },
    { icon: CheckCircle, label: t('login.trust2fa') },
  ];

  return (
    <div className="min-h-screen flex bg-app-bg relative">
      <div className="absolute top-4 right-4 z-20">
        <LanguageToggle />
      </div>
      {/* Panneau de marque (brand-aware, bleu-canard/vert forêt) */}
      <div className="hidden lg:flex lg:w-[44%] xl:w-2/5 bg-sidebar text-white flex-col justify-between p-12 xl:p-16">
        <Link to="/" className="inline-flex">
          <img src={brand.logo} alt={brand.name} className="h-14 w-auto" />
        </Link>
        <div className="max-w-sm">
          <h2 className="font-heading text-3xl xl:text-4xl font-bold tracking-tight leading-tight">
            {t('login.panelHeadline')}
          </h2>
          <p className="mt-4 text-white/70 leading-relaxed">{t('login.panelText')}</p>
          <ul className="mt-8 space-y-3">
            {trustPoints.map((p) => (
              <li key={p.label} className="flex items-center gap-3 text-sm text-white/85">
                <span className="h-8 w-8 rounded-lg bg-white/10 flex items-center justify-center">
                  <p.icon size={16} />
                </span>
                {p.label}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex items-center gap-2 text-xs text-white/50">
          <img src="/logo-pitboard.png" alt="Pitboard" className="h-4 w-auto opacity-80" />
          <span>{t('login.copyright')}</span>
        </div>
      </div>

      {/* Panneau formulaire */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md pb-reveal">
          <Link to="/" className="lg:hidden flex justify-center mb-8">
            <img src={brand.logo} alt={brand.name} className="h-16 w-auto" />
          </Link>

          <div className="text-center mb-8">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand-primary/10 text-brand-primary mb-4">
              <SpaceIcon size={24} />
            </div>
            <h1 className="font-heading text-2xl font-bold text-ink">{getSpaceTitle()}</h1>
            <p className="text-ink-secondary mt-1 text-sm">{t('login.subtitle')}</p>
          </div>

          <div className="bg-surface rounded-2xl shadow-sm border border-line p-7 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-ink mb-1.5">{t('login.emailLabel')}</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail size={18} className="text-ink-muted" />
                  </div>
                  <input
                    type="text"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    data-testid="login-email-input"
                    className="block w-full pl-10 pr-3 py-2.5 bg-app-bg border border-line rounded-lg text-ink placeholder:text-ink-muted focus:bg-surface focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary/40 outline-none transition-colors"
                    placeholder={t('login.emailPlaceholder')}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-ink mb-1.5">{t('login.passwordLabel')}</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock size={18} className="text-ink-muted" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    data-testid="login-password-input"
                    className="block w-full pl-10 pr-12 py-2.5 bg-app-bg border border-line rounded-lg text-ink placeholder:text-ink-muted focus:bg-surface focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary/40 outline-none transition-colors"
                    placeholder={t('login.passwordPlaceholder')}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-ink-muted hover:text-ink transition-colors"
                    data-testid="login-toggle-password"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-3 p-3.5 bg-critical/5 border border-critical/20 rounded-lg" data-testid="login-error">
                  <AlertCircle size={18} className="text-critical shrink-0 mt-0.5" />
                  <span className="text-critical text-sm whitespace-pre-line">{error}</span>
                </div>
              )}

              {success && (
                <div className="flex items-start gap-3 p-3.5 bg-success/5 border border-success/20 rounded-lg" data-testid="login-success">
                  <CheckCircle size={18} className="text-success shrink-0 mt-0.5" />
                  <span className="text-success text-sm whitespace-pre-line">{success}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit"
                className={`w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-white font-semibold transition-colors ${
                  loading ? 'bg-ink-muted cursor-not-allowed' : 'bg-brand-primary hover:bg-brand-primary-hover'
                }`}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/40 border-t-white" />
                    {t('login.submitting')}
                  </>
                ) : t('login.submit')}
              </button>

              <div className="text-center">
                <Link to={isAdminLogin ? '/forgot-password?admin=1' : '/forgot-password'} className="text-sm text-brand-primary hover:text-brand-primary-hover font-medium">
                  {t('login.forgot')}
                </Link>
              </div>
            </form>

            <div className="mt-6 pt-5 border-t border-line text-center">
              <p className="text-xs text-ink-muted flex items-center justify-center gap-1.5">
                <ShieldCheck size={14} /> {t('login.sslNote')}
              </p>
            </div>
          </div>

          <div className="mt-6 text-center">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-ink-secondary hover:text-brand-primary transition-colors">
              <ArrowLeft size={15} /> {t('login.back')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
